// Pausar e retomar o PowerAgent de UMA conversa — a mesma chamada para o botão do cabeçalho
// e para o painel. Um só, porque são dois lugares para a mesma ação, e a cópia que
// divergisse seria a que ninguém abre.
//
// Sem update otimista, de propósito: o espelho `additional_attributes.bot` tem um escritor
// só, o runtime (PLAN §1). O que a tela aplica ao store é a RESPOSTA do `PUT .../bot` — a
// linha lida de volta — e o realtime traz a mesma coisa em seguida. O toggle antigo
// (`toggleWorkforceBot`) faz o oposto, e é por isso que não é ele.
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';
import types from 'dashboard/store/mutation-types';
import { chaveDaOrigem, espelhoDoBot } from 'dashboard/helper/handoffTrace';

export function useAgentBot() {
  const store = useStore();
  const { t } = useI18n();

  // Por que o bot está pausado, em palavras. Chaves literais, uma por causa, e não
  // `t(\`...${chave}\`)`: o linter de i18n tem razão em não gostar de chave montada, e o
  // mapa fica aqui — o botão e o painel dizem a mesma frase pelo mesmo lugar.
  const CAUSAS = {
    HUMANO_RESPONDEU: () =>
      t('CONVERSATION.AGENT_BOT.PAUSED_BY.HUMANO_RESPONDEU'),
    TOOL: () => t('CONVERSATION.AGENT_BOT.PAUSED_BY.TOOL'),
    BOTAO: () => t('CONVERSATION.AGENT_BOT.PAUSED_BY.BOTAO'),
    API: () => t('CONVERSATION.AGENT_BOT.PAUSED_BY.API'),
    DORMENCIA: () => t('CONVERSATION.AGENT_BOT.PAUSED_BY.DORMENCIA'),
    BLOQUEIO: () => t('CONVERSATION.AGENT_BOT.PAUSED_BY.BLOQUEIO'),
    CONTINGENCIA: () => t('CONVERSATION.AGENT_BOT.PAUSED_BY.CONTINGENCIA'),
    UNKNOWN: () => t('CONVERSATION.AGENT_BOT.PAUSED_BY.UNKNOWN'),
  };

  function causaDaPausa(origem) {
    return `${t('CONVERSATION.AGENT_BOT.PAUSED_BY.LABEL')} ${CAUSAS[chaveDaOrigem(origem)]()}`;
  }

  // Resolve com a resposta do runtime; rejeita depois de avisar. Quem chama só precisa
  // decidir o que fazer com o sucesso (o painel relê; o botão não precisa de nada).
  async function definirAtivo(conversationId, active) {
    try {
      const { data } = await ConversationApi.setAgentBot(conversationId, {
        active,
      });
      const chat = store.getters.getSelectedChat;
      if (chat && String(chat.id) === String(conversationId)) {
        store.commit(types.UPDATE_CONVERSATION, {
          ...chat,
          additional_attributes: espelhoDoBot(chat.additional_attributes, data),
        });
      }
      useAlert(
        data.bot_active
          ? t('CONVERSATION.AGENT_BOT.RESUMED_OK')
          : t('CONVERSATION.AGENT_BOT.PAUSED_OK')
      );
      return data;
    } catch (e) {
      // "runtime fora do ar" e "não deu" não podem virar o mesmo aviso: no primeiro nada
      // mudou de propósito, e é isso que a pessoa precisa saber antes de clicar de novo.
      useAlert(
        e?.response?.data?.error === 'runtime_unreachable'
          ? t('CONVERSATION.AGENT_BOT.RUNTIME_DOWN')
          : t('CONVERSATION.AGENT_BOT.SAVE_ERROR')
      );
      throw e;
    }
  }

  return { definirAtivo, causaDaPausa };
}
